import {
  Provider,
  ProviderCategory,
  type SourceAccount,
} from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useDAL } from './useDAL';
import { useSourceAccounts } from './useSourceAccounts';

const toSortedProviders = (accounts: SourceAccount[] | undefined) =>
  Array.from(
    (accounts || []).reduce((acc, account) => {
      const provider = account.sourceAccountAttributes.cloudProvider;
      if (provider !== Provider.ProviderUnspecified) {
        acc.add(provider);
      }
      return acc;
    }, new Set<Provider>())
  ).sort();

export const useCloudProviders = () => {
  const { sourceAccounts } = useSourceAccounts();

  return useMemo(() => toSortedProviders(sourceAccounts), [sourceAccounts]);
};

// SaaS source accounts (e.g. Google Workspace) are excluded by the cloud-only
// default, so pass providerCategory explicitly to include them. Callers that
// only want cloud-platform accounts pass includeSaas=false.
export const useSourceProviders = (includeSaas = true) => {
  const dal = useDAL();
  const { data, isLoading } = dal.cloudAccounts.source.useList(
    undefined,
    undefined,
    {
      filters: {
        providerCategory: {
          _in: includeSaas
            ? [ProviderCategory.CloudPlatform, ProviderCategory.Saas]
            : [ProviderCategory.CloudPlatform],
        },
      },
    }
  );

  const accounts = data?.accounts;

  return useMemo(
    () => ({
      providers: toSortedProviders(accounts),
      accounts: accounts ?? [],
      isLoading,
    }),
    [accounts, isLoading]
  );
};

export const getAllCloudProviders = (isDemoOrDev: boolean) =>
  Object.values(Provider).filter(
    (x) =>
      x !== Provider.ProviderUnspecified &&
      x !== Provider.GoogleWorkspace &&
      x !== Provider.Microsoft365 &&
      (isDemoOrDev || x !== Provider.MongoAtlas)
  );
